/**
 * Unified Alerts Dashboard
 *
 * Aggregates all alert types (sanctions, PEP, adverse media, wallet screening,
 * transaction monitoring, market surveillance) in one place.
 *
 * Migrated to shadcn/ui + Hyperkit design system
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  Download,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  User,
  Shield,
  Eye,
  CheckSquare,
  ArrowUp,
  RefreshCw,
  X,
} from 'lucide-react';
import { useAlerts, useAlertsSummary, useResolveAlert, useEscalateAlert, useBatchResolveAlerts, useBatchEscalateAlerts } from '../hooks/useAlerts';
import { useTestMode } from '../contexts/TestModeContext';
import { useCreateCaseFromAlert } from '../hooks/useCases';
import { useAuth } from '../auth';
import { useToast } from '../hooks/useToast';
import AlertDetailPanel from './AlertDetailPanel';
import { formatDistanceToNow } from 'date-fns';
import { Button, Badge, Card, Spinner } from './ui';

// Severity badge variants
const severityVariants = {
  critical: 'destructive',
  high: 'warning',
  medium: 'pending',
  low: 'success',
};

// Category labels
const categoryLabels = {
  sanctions: 'Sanctions',
  pep: 'PEP',
  adverse_media: 'Adverse Media',
  wallet_screening: 'Wallet Screening',
  transaction_monitoring: 'Transaction Monitoring',
  market_surveillance: 'Market Surveillance',
  screening: 'Screening',
};

// Status badge variants
const statusVariants = {
  open: 'info',
  reviewing: 'purple',
  resolved: 'success',
  dismissed: 'muted',
  escalated: 'warning',
};

export default function AlertsDashboard() {
  // State
  const [filters, setFilters] = useState({
    status: 'open',
    category: '',
    severity: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [page, setPage] = useState(0);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showBatchEscalateModal, setShowBatchEscalateModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolutionAction, setResolutionAction] = useState('false_positive');
  const [escalationReason, setEscalationReason] = useState('');

  const { getToken } = useAuth();
  const toast = useToast();
  const { isTestMode } = useTestMode();

  const pageSize = 25;

  // Queries
  const { data: summary } = useAlertsSummary({ is_sandbox: isTestMode });
  const { data: alertsData, isLoading: alertsLoading, refetch } = useAlerts({
    ...filters,
    is_sandbox: isTestMode,
    limit: pageSize,
    offset: page * pageSize,
  });

  // Mutations
  const resolveAlert = useResolveAlert();
  const escalateAlert = useEscalateAlert();
  const batchResolve = useBatchResolveAlerts();
  const batchEscalate = useBatchEscalateAlerts();
  const createCaseFromAlert = useCreateCaseFromAlert();

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
    setSelectedAlerts([]);
  };

  const handleSelectAll = () => {
    if (selectedAlerts.length === alertsData?.items?.length) {
      setSelectedAlerts([]);
    } else {
      setSelectedAlerts(alertsData?.items?.map(a => a.id) || []);
    }
  };

  const handleSelectAlert = (id) => {
    setSelectedAlerts(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const handleResolve = async () => {
    if (!resolutionNotes.trim()) return;

    try {
      if (currentAlert) {
        await resolveAlert.mutateAsync({
          id: currentAlert.id,
          data: {
            resolution_action: resolutionAction,
            notes: resolutionNotes,
          },
        });
        toast.success('Alert resolved successfully');
      } else if (selectedAlerts.length > 0) {
        const result = await batchResolve.mutateAsync({
          alertIds: selectedAlerts,
          data: {
            resolution_action: resolutionAction,
            notes: resolutionNotes,
          },
        });
        toast.success(`${result.resolved_count} alerts resolved`);
        setSelectedAlerts([]);
      }

      setShowResolveModal(false);
      setCurrentAlert(null);
      setResolutionNotes('');
    } catch (error) {
      toast.error(error.message || 'Failed to resolve alert');
    }
  };

  const handleEscalate = async () => {
    if (!escalationReason.trim() || !currentAlert) return;

    try {
      await escalateAlert.mutateAsync({
        id: currentAlert.id,
        data: {
          reason: escalationReason,
        },
      });
      toast.success('Alert escalated to CCO');
      setShowEscalateModal(false);
      setCurrentAlert(null);
      setEscalationReason('');
    } catch (error) {
      toast.error(error.message || 'Failed to escalate alert');
    }
  };

  const handleBatchEscalate = async () => {
    if (!escalationReason.trim() || selectedAlerts.length === 0) return;

    try {
      const result = await batchEscalate.mutateAsync({
        alertIds: selectedAlerts,
        data: {
          reason: escalationReason,
        },
      });
      toast.success(`${result.escalated_count} alerts escalated to CCO`);
      setShowBatchEscalateModal(false);
      setEscalationReason('');
      setSelectedAlerts([]);
    } catch (error) {
      toast.error(error.message || 'Failed to escalate alerts');
    }
  };

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.status) params.set('status', filters.status);

    try {
      const token = await getToken();
      const response = await fetch(`/api/v1/alerts/export/csv?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'alerts_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      const totalCount = response.headers.get('X-Total-Count') || 'all';
      toast.success(`Exported ${totalCount} alerts to CSV`);
    } catch (error) {
      toast.error('Failed to export alerts');
      console.error('Export failed:', error);
    }
  };

  const openResolveModal = (alert = null) => {
    setCurrentAlert(alert);
    setShowResolveModal(true);
  };

  const openEscalateModal = (alert) => {
    setCurrentAlert(alert);
    setShowEscalateModal(true);
  };

  const handleCreateCaseFromAlert = async (alert) => {
    try {
      const newCase = await createCaseFromAlert.mutateAsync(alert.id);
      toast.success('Investigation case created');
      setSelectedAlert(null);
      window.location.href = `/cases/${newCase.id}`;
    } catch (error) {
      toast.error(error.message || 'Failed to create case');
    }
  };

  // Calculate pagination
  const totalPages = alertsData ? Math.ceil(alertsData.total / pageSize) : 0;

  return (
    <div className="flex flex-col gap-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-grey-900 dark:text-grey-50">Alerts</h1>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => refetch()}>
            <RefreshCw size={16} />
            Refresh
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            <Download size={16} />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-5">
          <p className="text-sm font-medium text-grey-500 dark:text-grey-400">Total Open</p>
          <p className="text-3xl font-bold text-grey-900 dark:text-grey-50 mt-1">{summary?.total_open || 0}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm font-medium text-grey-500 dark:text-grey-400">Critical</p>
          <p className="text-3xl font-bold text-red-500 mt-1">{summary?.critical || 0}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm font-medium text-grey-500 dark:text-grey-400">High Priority</p>
          <p className="text-3xl font-bold text-orange-500 mt-1">{summary?.high || 0}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm font-medium text-grey-500 dark:text-grey-400">Resolved Today</p>
          <p className="text-3xl font-bold text-green-500 mt-1">{summary?.resolved_today || 0}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 flex gap-3 items-center flex-wrap">
        <Filter size={18} className="text-grey-400" />
        <select
          className="px-3 py-2 rounded-md bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 min-w-[140px] cursor-pointer"
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="reviewing">Reviewing</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
          <option value="escalated">Escalated</option>
        </select>

        <select
          className="px-3 py-2 rounded-md bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 min-w-[140px] cursor-pointer"
          value={filters.category}
          onChange={(e) => handleFilterChange('category', e.target.value)}
        >
          <option value="">All Categories</option>
          <option value="sanctions">Sanctions</option>
          <option value="pep">PEP</option>
          <option value="adverse_media">Adverse Media</option>
          <option value="wallet_screening">Wallet Screening</option>
          <option value="transaction_monitoring">Transaction Monitoring</option>
          <option value="screening">General Screening</option>
        </select>

        <select
          className="px-3 py-2 rounded-md bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 min-w-[140px] cursor-pointer"
          value={filters.severity}
          onChange={(e) => handleFilterChange('severity', e.target.value)}
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </Card>

      {/* Alerts Table */}
      <Card className="overflow-hidden">
        {alertsLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" className="text-teal-500" />
          </div>
        ) : alertsData?.items?.length === 0 ? (
          <div className="text-center py-16 px-5">
            <CheckCircle size={48} className="text-grey-300 dark:text-grey-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-grey-900 dark:text-grey-50 mb-2">No alerts found</h3>
            <p className="text-grey-500 dark:text-grey-400">All clear! No alerts match your current filters.</p>
          </div>
        ) : (
          <>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-grey-50 dark:bg-grey-800/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700 w-10">
                    <input
                      type="checkbox"
                      className="w-4 h-4 cursor-pointer accent-teal-500"
                      checked={selectedAlerts.length === alertsData?.items?.length && alertsData?.items?.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Severity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Applicant</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700">Created</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider border-b border-grey-200 dark:border-grey-700 w-14"></th>
                </tr>
              </thead>
              <tbody>
                {alertsData?.items?.map((alert) => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    selected={selectedAlerts.includes(alert.id)}
                    onSelect={() => handleSelectAlert(alert.id)}
                    onResolve={() => openResolveModal(alert)}
                    onEscalate={() => openEscalateModal(alert)}
                    onRowClick={() => setSelectedAlert(alert)}
                  />
                ))}
              </tbody>
            </table>

            {/* Batch Actions */}
            {selectedAlerts.length > 0 && (
              <div className="flex items-center gap-4 px-4 py-3 bg-grey-50 dark:bg-grey-800/50 border-t border-grey-200 dark:border-grey-700">
                <span className="text-sm text-grey-500 dark:text-grey-400">
                  {selectedAlerts.length} selected
                </span>
                <Button size="sm" onClick={() => openResolveModal(null)}>
                  <CheckSquare size={14} />
                  Resolve Selected
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setShowBatchEscalateModal(true)}>
                  <ArrowUp size={14} />
                  Escalate Selected
                </Button>
              </div>
            )}

            {/* Pagination */}
            <div className="flex justify-between items-center px-4 py-3 border-t border-grey-200 dark:border-grey-700">
              <p className="text-sm text-grey-500 dark:text-grey-400">
                Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, alertsData?.total || 0)} of {alertsData?.total || 0}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                >
                  <ChevronLeft size={16} />
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Resolve Modal */}
      {showResolveModal && (
        <Modal onClose={() => setShowResolveModal(false)}>
          <ModalHeader onClose={() => setShowResolveModal(false)}>
            {currentAlert ? 'Resolve Alert' : `Resolve ${selectedAlerts.length} Alerts`}
          </ModalHeader>
          <div className="p-5 space-y-4">
            {currentAlert && (
              <div className="bg-grey-50 dark:bg-grey-800 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-grey-500 dark:text-grey-400">Category</span>
                  <span className="font-medium text-grey-900 dark:text-grey-50">{categoryLabels[currentAlert.category] || currentAlert.category}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-grey-500 dark:text-grey-400">Severity</span>
                  <Badge variant={severityVariants[currentAlert.severity]}>{currentAlert.severity}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-grey-500 dark:text-grey-400">Applicant</span>
                  <span className="font-medium text-grey-900 dark:text-grey-50">{currentAlert.applicant?.full_name || 'Unknown'}</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-grey-900 dark:text-grey-50 mb-2">
                Resolution Action
              </label>
              <select
                className="w-full px-3 py-2 rounded-md bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                value={resolutionAction}
                onChange={(e) => setResolutionAction(e.target.value)}
              >
                <option value="false_positive">Dismiss as False Positive</option>
                <option value="confirmed_match">Confirm Match</option>
                <option value="no_action">No Action Required</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-grey-900 dark:text-grey-50 mb-2">
                Resolution Notes *
              </label>
              <textarea
                className="w-full px-3 py-2 rounded-md bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-y min-h-[100px]"
                placeholder="Explain your resolution decision..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
              />
            </div>
          </div>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setShowResolveModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={!resolutionNotes.trim() || resolveAlert.isPending || batchResolve.isPending}
            >
              {resolveAlert.isPending || batchResolve.isPending ? 'Resolving...' : 'Resolve Alert'}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Escalate Modal */}
      {showEscalateModal && currentAlert && (
        <Modal onClose={() => setShowEscalateModal(false)}>
          <ModalHeader onClose={() => setShowEscalateModal(false)}>
            Escalate Alert
          </ModalHeader>
          <div className="p-5 space-y-4">
            <div className="bg-grey-50 dark:bg-grey-800 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-grey-500 dark:text-grey-400">Category</span>
                <span className="font-medium text-grey-900 dark:text-grey-50">{categoryLabels[currentAlert.category] || currentAlert.category}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-grey-500 dark:text-grey-400">Severity</span>
                <Badge variant={severityVariants[currentAlert.severity]}>{currentAlert.severity}</Badge>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-grey-900 dark:text-grey-50 mb-2">
                Escalation Reason *
              </label>
              <textarea
                className="w-full px-3 py-2 rounded-md bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-y min-h-[100px]"
                placeholder="Explain why this alert needs to be escalated to CCO..."
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
              />
            </div>
          </div>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setShowEscalateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEscalate}
              disabled={!escalationReason.trim() || escalateAlert.isPending}
            >
              {escalateAlert.isPending ? 'Escalating...' : 'Escalate to CCO'}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Batch Escalate Modal */}
      {showBatchEscalateModal && (
        <Modal onClose={() => setShowBatchEscalateModal(false)}>
          <ModalHeader onClose={() => setShowBatchEscalateModal(false)}>
            Escalate {selectedAlerts.length} Alerts
          </ModalHeader>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-grey-900 dark:text-grey-50 mb-2">
                Escalation Reason *
              </label>
              <textarea
                className="w-full px-3 py-2 rounded-md bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-y min-h-[100px]"
                placeholder="Explain why these alerts need to be escalated to CCO..."
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
              />
            </div>
          </div>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setShowBatchEscalateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBatchEscalate}
              disabled={!escalationReason.trim() || batchEscalate.isPending}
            >
              {batchEscalate.isPending ? 'Escalating...' : `Escalate ${selectedAlerts.length} Alerts`}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Alert Detail Panel */}
      {selectedAlert && (
        <AlertDetailPanel
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onResolve={() => {
            setSelectedAlert(null);
            openResolveModal(selectedAlert);
          }}
          onEscalate={() => {
            setSelectedAlert(null);
            openEscalateModal(selectedAlert);
          }}
          onCreateCase={() => handleCreateCaseFromAlert(selectedAlert)}
          isCreatingCase={createCaseFromAlert.isPending}
        />
      )}
    </div>
  );
}

/**
 * Modal Components
 */
function Modal({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-grey-900 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ children, onClose }) {
  return (
    <div className="flex justify-between items-center px-5 py-4 border-b border-grey-200 dark:border-grey-700">
      <h2 className="text-lg font-semibold text-grey-900 dark:text-grey-50">{children}</h2>
      <button
        onClick={onClose}
        className="text-grey-400 hover:text-grey-600 dark:hover:text-grey-200 text-2xl leading-none"
      >
        <X size={20} />
      </button>
    </div>
  );
}

function ModalFooter({ children }) {
  return (
    <div className="flex justify-end gap-3 px-5 py-4 border-t border-grey-200 dark:border-grey-700">
      {children}
    </div>
  );
}

/**
 * Alert Row Component
 */
function AlertRow({ alert, selected, onSelect, onResolve, onEscalate, onRowClick }) {
  const [showActions, setShowActions] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowActions(false);
      }
    };
    if (showActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActions]);

  const handleRowClick = (e) => {
    if (
      e.target.closest('input[type="checkbox"]') ||
      e.target.closest('a') ||
      e.target.closest('.action-menu')
    ) {
      return;
    }
    onRowClick?.();
  };

  const statusIcons = {
    open: Clock,
    resolved: CheckCircle,
    escalated: ArrowUp,
  };
  const StatusIcon = statusIcons[alert.status];

  return (
    <tr
      onClick={handleRowClick}
      className="cursor-pointer hover:bg-grey-50 dark:hover:bg-grey-800/50 border-b border-grey-200 dark:border-grey-700 last:border-b-0"
    >
      <td className="px-4 py-3">
        <input
          type="checkbox"
          className="w-4 h-4 cursor-pointer accent-teal-500"
          checked={selected}
          onChange={onSelect}
        />
      </td>
      <td className="px-4 py-3">
        <Badge variant={severityVariants[alert.severity] || 'default'} className="inline-flex items-center gap-1.5">
          <AlertCircle size={12} />
          {alert.severity?.charAt(0).toUpperCase() + alert.severity?.slice(1)}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <Badge variant="default" className="inline-flex items-center gap-1.5">
          <Shield size={12} />
          {categoryLabels[alert.category] || alert.category}
        </Badge>
      </td>
      <td className="px-4 py-3">
        {alert.applicant ? (
          <a
            href={`/applicants/${alert.applicant_id}`}
            className="inline-flex items-center gap-2 text-teal-500 hover:text-teal-600 hover:underline font-medium text-sm"
          >
            <User size={14} />
            {alert.applicant.full_name || 'Unknown'}
          </a>
        ) : (
          <span className="text-grey-400 text-sm">Unknown</span>
        )}
      </td>
      <td className="px-4 py-3">
        <Badge variant={statusVariants[alert.status] || 'default'} className="inline-flex items-center gap-1">
          {StatusIcon && <StatusIcon size={12} />}
          {alert.status?.charAt(0).toUpperCase() + alert.status?.slice(1)}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-grey-500 dark:text-grey-400">
          {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="action-menu relative" ref={dropdownRef}>
          <button
            className="p-1.5 rounded-md hover:bg-grey-100 dark:hover:bg-grey-700 text-grey-400 hover:text-grey-600 dark:hover:text-grey-200"
            onClick={() => setShowActions(!showActions)}
          >
            <MoreVertical size={18} />
          </button>
          {showActions && (
            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg shadow-lg min-w-[160px] z-50 overflow-hidden">
              <button
                className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm text-grey-700 dark:text-grey-200 hover:bg-grey-50 dark:hover:bg-grey-700"
                onClick={() => window.location.href = `/applicants/${alert.applicant_id}`}
              >
                <Eye size={16} className="text-grey-400" />
                View Applicant
              </button>
              {alert.status !== 'resolved' && alert.status !== 'dismissed' && (
                <>
                  <button
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm text-grey-700 dark:text-grey-200 hover:bg-grey-50 dark:hover:bg-grey-700"
                    onClick={() => { setShowActions(false); onResolve(); }}
                  >
                    <CheckCircle size={16} className="text-grey-400" />
                    Resolve
                  </button>
                  <button
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm text-grey-700 dark:text-grey-200 hover:bg-grey-50 dark:hover:bg-grey-700"
                    onClick={() => { setShowActions(false); onEscalate(); }}
                  >
                    <ArrowUp size={16} className="text-grey-400" />
                    Escalate
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}
